/* eslint semi:0 */
/**
 * Wraps window apis for cross-browser and mocking.
 */
import objectPath from 'object-path'
import { info } from '../application/console'
import { noop, areAllFunctions } from './fn'
import { create as makeEmptyObj, deepFreeze } from './object'
import { registerInNamespace, registerUtils } from '../application/nm'
import { capitalizeFirstLetter, unCapitalizeFirstLetter } from './string'

function dashDelimitedToCamelCase(string) {
  return string.replace(/-([a-z])/g, ({ 1: firstLetterAfterDash }) =>
    firstLetterAfterDash.toUpperCase()
  )
}

function decode(string) {
  return decodeURIComponent(string.replace(/\+/g, ' '))
}

function deserializeQueryString(inputQuery, queryStringObj = makeEmptyObj()) {
  const search = /([^&=]+)=?([^&]*)/g
  const query = inputQuery.substring(1)
  let matches = search.exec(query)
  while (matches) {
    const [fullKey, onlyKey, rawValue] = matches
    const isOnlyKey = fullKey === onlyKey
    const value = isOnlyKey ? true : decode(rawValue)
    const key = dashDelimitedToCamelCase(onlyKey)
    queryStringObj[key] = value
    matches = search.exec(query)
  }
  return queryStringObj
}

const POPUP_DEFAULTS = deepFreeze({
  url: null,
  title: '',
  width: 350,
  height: 350,
  event: null,
})

export const hash = deserializeQueryString(window.location.hash)
const qs = deserializeQueryString(window.location.search, hash)

function getStackTrace() {
  try {
    throw Error()
  } catch (ex) {
    return ex.stack.split('\n').slice(3).map(l => l.replace('at ', '').trim())
  }
}

function proxyQueryStringMaybe(queryString) {
  if (!('Proxy' in window) || !queryString.captureQueryStrings) {
    return queryString
  }
  const checkedQueries = new Map()
  Object.defineProperties(queryString, {
    checkedQueries: {
      value: checkedQueries,
    },
    checkedQueriesMap: {
      get() {
        return Array.from(checkedQueries.entries()).reduce(
          (queries, [query, stack]) => Object.assign(queries, { [query]: stack }),
          makeEmptyObj()
        )
      },
    },
  })
  return new Proxy(queryString, {
    get(target, key, reciever) {
      if (!(key in target && !checkedQueries.has(key))) {
        checkedQueries.set(key, getStackTrace())
      }
      return Reflect.get(...arguments)
    },
  })
}

export const queryString = proxyQueryStringMaybe(qs)
const queryStringPath = objectPath(queryString)

registerInNamespace({ queryString })

function prefixQsProperty(propName, prefix = '') {
  return `${prefix}${prefix ? capitalizeFirstLetter(propName) : propName}`
}

export function unprefixQsProperty(prefix, prefixedPropName) {
  return unCapitalizeFirstLetter(prefixedPropName.replace(prefix, ''))
}

const debugMessages = makeEmptyObj()

export function getPossiblyOverriddenPropByQsWithPrefix(prefix, propNameToOverride, realValue) {
  const propName = prefixQsProperty(propNameToOverride, prefix)
  const possiblyOverridenValue = queryStringPath.get(propName, realValue)
  const originalValue =
    ([undefined, window].includes(this) ? realValue : this[propNameToOverride]) || realValue
  // info({ propNameToOverride, originalValue, realValue, })
  if (possiblyOverridenValue !== originalValue && !(propNameToOverride in debugMessages)) {
    debugMessages[propNameToOverride] = true
    const isAdditionalValue = originalValue === undefined
    const verb = isAdditionalValue ? 'add' : 'override'
    const realValueString = isAdditionalValue ? 'with value' : `of '${originalValue}' with`
    info(
      `%c[QS] ${verb} '${propNameToOverride}' ${realValueString}`,
      'font-style: italic; color: red;',
      possiblyOverridenValue
    )
  }
  return possiblyOverridenValue
}

function addMatchMediaEvent(mediaQuery, matchChangeHandler, mismatchChangeHandler = noop) {
  if (!areAllFunctions(window.matchMedia, matchChangeHandler, mismatchChangeHandler)) {
    return false
  }
  const handler = function onMatchMediaChangeHandler(mql) {
    const handlerToCall = mql.matches ? matchChangeHandler : mismatchChangeHandler
    return handlerToCall(mql)
  }
  const mql = window.matchMedia(mediaQuery)
  mql.addListener(handler)
  return {
    mql,
    handler,
  }
}

export function removeMatchMediaEvent(mqlAndHandlerObj) {
  const { mql, handler } = mqlAndHandlerObj
  if (mql && areAllFunctions(mql.removeListener, handler)) {
    mql.removeListener(handler)
  }
}

export function addOnOrientationChangeEvent(onPortrait, onLandscape) {
  if (!areAllFunctions(onPortrait, onLandscape)) {
    return false
  }
  return addMatchMediaEvent('(orientation: portrait)', onPortrait, onLandscape)
}

export function addOnLandscapeChangeEvent(onLandscape) {
  return addOnOrientationChangeEvent(noop, onLandscape)
}

export function openPopup(options) {
  const { url, title, width, height, event: ev } = {
    ...makeEmptyObj(),
    ...POPUP_DEFAULTS,
    ...options,
  }
  ev && ev.preventDefault()
  window.open(url, title, `width=${width} ,height=${height}`)
}

export function navigateTo(url) {
  window.location = url
  return url
}

export const path = window.location.pathname

export const addWindowEvent = window.addEventListener.bind(window)
const removeWindowEvent = window.removeEventListener.bind(window)

const windw = Object.assign(makeEmptyObj(), {
  openPopup,
  queryString,
  queryStringPath,
  hash,
  path,
  navigateTo,
  prefixQsProperty,
  unprefixQsProperty,
  getPossiblyOverriddenPropByQsWithPrefix,
  addWindowEvent,
  removeWindowEvent,
  addMatchMediaEvent,
  removeMatchMediaEvent,
  addOnOrientationChangeEvent,
  addOnLandscapeChangeEvent,
})

registerUtils({ window: windw })

export default windw



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/utils/window.js